import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { Product } from '../types';
import { supabaseStorage } from '../services/supabase';

interface ProductImageItem {
  id: string | number;
  image: string;
  is_main?: boolean;
}

interface ImageManagerProps {
  product?: Product;
  existingImages: ProductImageItem[];
  setExistingImages: React.Dispatch<React.SetStateAction<ProductImageItem[]>>;
  onImageSelect?: (imageUrl: string) => void;
  currentImage?: string;
  // Nuevas props para selección múltiple
  multiple?: boolean;
  selectedImages?: string[];
  onSelectionChange?: (images: string[]) => void;
}

const ImageManager: React.FC<ImageManagerProps> = ({
  product,
  existingImages,
  setExistingImages,
  onImageSelect,
  currentImage,
  multiple = false,
  selectedImages = [],
  onSelectionChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketError, setBucketError] = useState('');
  const [bucketImages, setBucketImages] = useState<Array<{ name: string; url: string; size: number; createdAt: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // estilos simples para asegurar grilla y thumbnails
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: 12,
  };
  const tileStyle: React.CSSProperties = {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    background: '#fff',
    position: 'relative',
  };
  const imgStyle: React.CSSProperties = {
    width: '100%',
    height: 120,
    objectFit: 'cover'
  };

  // Eliminar imagen del bucket
  const handleDeleteBucketImage = async (path: string) => {
    const ok = window.confirm('¿Eliminar esta imagen del bucket permanentemente?');
    if (!ok) return;
    try {
      setBucketLoading(true);
      setBucketError('');
      const result = await supabaseStorage.deleteImage(path);
      if (!('success' in result) || !result.success) {
        setBucketError((result as any)?.error || 'No se pudo eliminar la imagen');
        return;
      }
      const refresh = await supabaseStorage.listImagesDeep('products');
      if (refresh.success) setBucketImages(refresh.images);
    } catch (err: any) {
      setBucketError(err?.message || 'Error al eliminar imagen del bucket');
    } finally {
      setBucketLoading(false);
    }
  };

  // Listar imágenes disponibles en el bucket (recursivo)
  useEffect(() => {
    const loadBucket = async () => {
      try {
        setBucketLoading(true);
        setBucketError('');
        const result = await supabaseStorage.listImagesDeep('products');
        if (result.success) {
          setBucketImages(result.images);
        } else {
          setBucketImages([]);
          setBucketError(result.error);
        }
      } catch (err: any) {
        setBucketError(err?.message || 'Error al listar imágenes del bucket');
      } finally {
        setBucketLoading(false);
      }
    };
    loadBucket();
  }, [product?.id]);

  const handleDeleteImage = async (imageId: string | number, keepFile: boolean) => {
    const message = keepFile 
      ? '¿Solo desvincular esta imagen del producto? (Se mantendrá en el bucket)'
      : '¿Eliminar PERMANENTEMENTE esta imagen del bucket y del producto?';

    if (!window.confirm(message)) {
      return;
    }
    setLoading(true);
    try {
      const url = `products/images/${imageId}/${keepFile ? '?keep_file=true' : ''}`;
      await api.delete(url);
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
      alert(keepFile ? 'Imagen desvinculada correctamente' : 'Imagen eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      alert('Error al eliminar la imagen. Revisa la consola.');
    } finally {
      setLoading(false);
    }
  };

  // Subir imagen al bucket y seleccionarla
  const handleUpload = async (file?: File) => {
    if (!file) return;
    setBucketLoading(true);
    setBucketError('');
    try {
      const fileName = file.name;
      const result = await supabaseStorage.uploadImage(file, fileName);
      if (!result.success) {
        setBucketError(result.error);
        return;
      }
      // Refrescar listado del bucket
      const refresh = await supabaseStorage.listImagesDeep('products');
      if (refresh.success) setBucketImages(refresh.images);
      
      // Seleccionar la imagen subida
      if (multiple && onSelectionChange) {
        onSelectionChange([...selectedImages, result.publicUrl]);
      } else {
        onImageSelect?.(result.publicUrl);
      }
    } catch (err: any) {
      setBucketError(err?.message || 'Error al subir imagen');
    } finally {
      setBucketLoading(false);
    }
  };

  const handleImageClick = (url: string) => {
    if (multiple && onSelectionChange) {
      if (selectedImages.includes(url)) {
        onSelectionChange(selectedImages.filter(u => u !== url));
      } else {
        onSelectionChange([...selectedImages, url]);
      }
    } else {
      onImageSelect?.(url);
    }
  };

  return (
    <div className="image-manager">
      {/* Imágenes ya asociadas al producto (si existen) */}
      {existingImages && existingImages.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Imágenes actuales del producto</h4>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {existingImages.map((img) => (
              <div key={img.id} className="relative flex-shrink-0 w-32 h-32 border border-gray-200 rounded-lg overflow-hidden bg-white group">
                <img src={img.image} alt="Product" className="w-full h-full object-cover" />
                {img.is_main && (
                  <span className="absolute bottom-0 inset-x-0 bg-gray-900/80 text-white text-xs py-1 text-center">
                    Principal
                  </span>
                )}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    type="button"
                    onClick={() => handleDeleteImage(img.id, true)}
                    className="bg-yellow-500 text-white p-1.5 rounded-full shadow-sm hover:bg-yellow-600"
                    title="Desvincular (Mantener en bucket)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button 
                    type="button"
                    onClick={() => handleDeleteImage(img.id, false)}
                    className="bg-red-600 text-white p-1.5 rounded-full shadow-sm hover:bg-red-700"
                    title="Eliminar PERMANENTEMENTE"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Banco de imágenes (bucket Supabase) */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h4>Imágenes disponibles en el bucket {multiple && '(Selecciona varias)'}</h4>
          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={bucketLoading}
              className="px-3 py-2 bg-gray-900 text-white rounded hover:bg-gray-800 disabled:opacity-60"
            >
              Subir imagen
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              style={{ display: 'none' }}
            />
          </div>
        </div>
        {bucketLoading && <p>Cargando imágenes del bucket...</p>}
        {bucketError && <p style={{ color: 'red' }}>{bucketError}</p>}
        <div style={{ ...gridStyle, maxHeight: 320, overflowY: 'auto', paddingRight: 8 }}>
          {bucketImages.map((img) => {
            const isSelected = multiple ? selectedImages.includes(img.url) : currentImage === img.url;
            return (
              <div 
                key={img.name} 
                style={{
                  ...tileStyle,
                  border: isSelected ? '3px solid #111827' : '1px solid #e5e7eb',
                  opacity: isSelected ? 1 : 0.8
                }} 
                onClick={() => handleImageClick(img.url)}
              >
                <img src={img.url} alt={img.name} style={imgStyle} />
                {isSelected && (
                  <div className="absolute top-1 right-1 bg-gray-900 text-white rounded-full p-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <div style={{ padding: 8, display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteBucketImage(img.name);
                    }}
                    disabled={bucketLoading}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            );
          })}
          {bucketImages.length === 0 && !bucketLoading && !bucketError && (
            <p style={{ color: '#666' }}>No se encontraron imágenes en el bucket.</p>
          )}
        </div>
      </div>
      
      {/* Previsualización de selección */}
      {!multiple && currentImage && (
        <div className="current-image-preview">
          <h4>Imagen principal seleccionada</h4>
          <img src={currentImage} alt="Actual" style={{ width: '100%', maxWidth: 520, height: 'auto' }} />
        </div>
      )}
      
      {multiple && selectedImages.length > 0 && (
        <div className="selected-images-preview mt-4">
          <h4>Imágenes seleccionadas ({selectedImages.length})</h4>
          <div className="flex gap-2 overflow-x-auto py-2">
            {selectedImages.map((url, idx) => (
              <div key={idx} className="relative flex-shrink-0 w-24 h-24 border border-gray-200 rounded-lg overflow-hidden">
                <img src={url} alt={`Selected ${idx}`} className="w-full h-full object-cover" />
                <button 
                  type="button"
                  onClick={() => handleImageClick(url)}
                  className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageManager;